import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ReactComponent as BackIcon} from '@/components/assets/icons/back.svg';
import globalStore from '@/services/global.state';
import {goBack, goTo} from '@/utils';
import {useRechargeCommissionRules} from '../invitation/home/commission-rule.hooks';
import {InvitationRulesContent} from '../invitation/home/invitation-rules-popup';

const normalizeAgentLevel = (value: unknown) => {
  const level = Number(value);
  return Number.isFinite(level) && level > 0 ? level : undefined;
};

const AgentRules = () => {
  const {t} = useTranslation();
  const [agentLevel, setAgentLevel] = useState<number | undefined>(() =>
    normalizeAgentLevel(globalStore.userInfo?.agentLevel),
  );
  const commissionRules = useRechargeCommissionRules();
  const {rechargeRuleGroups, ruleThresholds} = commissionRules;

  useEffect(() => {
    const redirectIfAgent = () => {
      if (globalStore.token && globalStore.userInfo?.isAgent === 1) {
        goTo('ProxyHome');
      }
    };
    redirectIfAgent();
    const tokenSub = globalStore.tokenSubject.subscribe(() => {
      redirectIfAgent();
    });
    const userSub = globalStore.userSubject.subscribe(() => {
      redirectIfAgent();
    });
    return () => {
      tokenSub.unsubscribe();
      userSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let active = true;
    const userSubscription = globalStore.userSubject.subscribe(user => {
      if (active) {
        setAgentLevel(normalizeAgentLevel(user?.agentLevel));
      }
    });

    globalStore.asyncGetItem('user').then(value => {
      if (!active || !value) {
        return;
      }
      try {
        const user = JSON.parse(value) as {agentLevel?: number};
        setAgentLevel(normalizeAgentLevel(user.agentLevel));
      } catch {
        setAgentLevel(undefined);
      }
    });

    return () => {
      active = false;
      userSubscription.unsubscribe();
    };
  }, []);

  return (
    <main className="fixed inset-y-0 left-1/2 flex min-h-0 w-full max-w-[23.4375rem] -translate-x-1/2 flex-col overflow-hidden bg-[linear-gradient(180deg,#230d07_0%,#2e140b_100%)]">
      <header className="relative flex h-[3.25rem] shrink-0 items-center justify-center bg-[rgb(8,7,6)] px-3 shadow-[0_0.125rem_0.5rem_0.25rem_rgba(0,0,0,0.28)]">
        <button
          aria-label={t('label.back')}
          className="absolute inset-y-0 left-3 flex w-6 items-center justify-center border-0 bg-transparent p-0 text-white"
          type="button"
          onClick={goBack}>
          <BackIcon className="size-4" />
        </button>
        <h1 className="m-0 text-base font-bold leading-none text-white">
          {t('proxy.home.invitation-rules')}
        </h1>
      </header>
      <div className="min-h-0 flex-1 touch-pan-y overflow-x-hidden overflow-y-auto [overscroll-behavior-y:contain] [-webkit-overflow-scrolling:touch]">
        <InvitationRulesContent
          agentLevel={agentLevel}
          rechargeRuleGroups={rechargeRuleGroups}
          standalone
          thresholds={ruleThresholds}
        />
      </div>
    </main>
  );
};

export default AgentRules;
